<template>
  <global-navigation>
    <template v-slot:side-top>
      <nav>
        <router-link
          v-for="{ label, name, icon, attention, isDisabled } in navItems"
          :key="name"
          :to="{ name }"
          :class="['nav-item', { attention }, { disabled: isDisabled }]"
          active-class="active"
        >
          <span :class="['nav-item-icon', { attention }]">
            <icon :name="icon" class="icon" />
          </span>
          <span :class="['nav-item-label', { attention }]">{{ label }}</span>
        </router-link>
      </nav>
    </template>
    <template v-slot:side-bottom>
      <live-work-stats v-if="isWorking" />
      <button
        type="button"
        :class="['nav-item', { 'active-icon': isDarkModeOn }]"
        @click="toggleDarkMode"
      >
        <span class="nav-item-icon">
          <icon name="dark-mode" class="icon" />
        </span>
        <span class="nav-item-label">
          {{ $t("navigation.darkMode") }}
        </span>
      </button>
      <button
        type="button"
        :class="['nav-item', { 'active-icon': !isSoundOn }]"
        @click="toggleSound"
      >
        <span class="nav-item-icon">
          <icon :name="soundBtnIcon" class="icon" />
        </span>
        <span class="nav-item-label">
          {{ $t("navigation.sound") }}
        </span>
      </button>
      <button
        type="button"
        :class="[
          'nav-item',
          {
            danger: isWorking,
            warning: isWorkChanging || isReconnecting,
            disabled: isReconnecting || !isOnline
          }
        ]"
        @click="workToggle"
        :disabled="isWorkChanging || isReconnecting || !isOnline"
      >
        <span class="nav-item-icon">
          <icon name="power" class="icon" />
        </span>
        <span class="nav-item-label">
          {{ workBtnText }}
        </span>
      </button>
      <button type="button" class="nav-item" @click="signOut">
        <span class="nav-item-icon">
          <icon name="logout" class="icon" />
        </span>
        <span class="nav-item-label">
          {{ $t("navigation.logout") }}
        </span>
      </button>
    </template>
    <router-view />
    <identification-focus-handler v-if="isWorking" />
    <offline-detect />
  </global-navigation>
</template>

<script>
import GlobalNavigation from "@/components/GlobalNavigation.vue";
import IdentificationFocusHandler from "@/components/IdentificationFocusHandler";
import { mapActions, mapGetters, mapState } from "vuex";
import { ROUTE_NAMES } from "@/router/constants";
import { actions, getters } from "@/store";
import { userManager } from "@/utils/identity";
import { CLOSE_REASON, WORK_STATUS } from "@/constants";
import {
  addUnloadListener,
  removeUnloadListener
} from "@/utils/unloadListeners";
import LiveWorkStats from "@/components/LiveWorkStats";
import { toggleDarkModeTheme } from "@/utils/theme";
import { toggleAllElementsSound } from "@/utils/media";
import OfflineDetect from "./OfflineDetect.vue";

export default {
  data() {
    return { isSoundOn: true, isDarkModeOn: false };
  },
  components: {
    LiveWorkStats,
    GlobalNavigation,
    IdentificationFocusHandler,
    OfflineDetect
  },
  mounted() {
    this.$nextTick(() => {
      this.isDarkModeOn = document.documentElement.classList.contains(
        "dark-mode"
      );
    });

    addUnloadListener(this.unloadHandler);
  },
  beforeDestroy() {
    removeUnloadListener(this.unloadHandler);
  },
  computed: {
    ...mapState({
      work: state => state.work
    }),
    ...mapGetters([
      getters.isOnline,
      getters.isWorking,
      getters.isWorkChanging,
      getters.isReconnecting,
      getters.currentIdentification,
      getters.hasComplianceReviewRole
    ]),
    navItems() {
      const items = [
        {
          name: ROUTE_NAMES.DASHBOARD,
          label: this.$t("navigation.dashboard"),
          icon: "view-dashboard"
        },
        {
          name: ROUTE_NAMES.IDENTIFICATION,
          attention: this.identificationNeedsAttention,
          label: this.$t("navigation.identification"),
          icon: "account-question"
        },
        this.hasComplianceReviewRole && {
          name: ROUTE_NAMES.COMPLIANCE_REVIEW_SEARCH,
          label: this.$t("navigation.reviews"),
          icon: "compliance-review",
          isDisabled: this.isWorking || this.isWorkChanging
        },
        {
          name: ROUTE_NAMES.SANCTION_SEARCH,
          label: this.$t("navigation.screening"),
          icon: "account-search"
        },
        {
          name: ROUTE_NAMES.TYPE_GAME,
          label: this.$t("navigation.typeGame"),
          icon: "type-game"
        }
      ];

      return items.filter(item => !!item);
    },
    hasActiveIdentification() {
      return !!this.currentIdentification?.identificationId;
    },
    isIdentificationRoute() {
      return this.$route.matched.some(
        ({ name }) => name === ROUTE_NAMES.IDENTIFICATION
      );
    },
    identificationNeedsAttention() {
      return (
        this.isWorking &&
        this.hasActiveIdentification &&
        !this.isIdentificationRoute
      );
    },
    soundBtnIcon() {
      return this.isSoundOn ? "soundOn" : "soundOff";
    },
    workBtnText() {
      switch (this.work.status) {
        case WORK_STATUS.WORKING:
          return this.$t("navigation.work_stop");
        case WORK_STATUS.RECONNECTING:
          return this.$t("navigation.work_reconnecting");
        case WORK_STATUS.STOPPING:
          return this.$t("navigation.work_stopping");
        case WORK_STATUS.STARTING:
          return this.$t("navigation.work_starting");
        case WORK_STATUS.NOT_STARTED:
        case WORK_STATUS.STOPPED:
        default:
          return this.$t("navigation.work_start");
      }
    }
  },
  methods: {
    ...mapActions([actions.workStart, actions.workStop]),
    toggleSound() {
      toggleAllElementsSound(this.isSoundOn);
      this.isSoundOn = !this.isSoundOn;
    },
    async signOut() {
      await userManager.signoutRedirect();
    },
    toggleDarkMode() {
      toggleDarkModeTheme(!this.isDarkModeOn);
      this.isDarkModeOn = !this.isDarkModeOn;
    },
    workToggle() {
      this.$trackEvent({
        name: "userAction/workToggle",
        properties: {
          text: this.workBtnText
        }
      });
      if (this.isWorking) {
        this.workStop(CLOSE_REASON.STOP_BUTTON);
      } else {
        this.workStart();
        if (!this.isIdentificationRoute) {
          this.$router.push({ name: ROUTE_NAMES.IDENTIFICATION });
        }
      }
    },
    unloadHandler(event) {
      if (this.isWorking) {
        event.preventDefault();
        event.returnValue = "";
      }
    }
  }
};
</script>

<style scoped lang="scss">
@keyframes attention {
  0% {
    color: var(--text-color-base);
  }
  100% {
    color: var(--text-danger);
    filter: drop-shadow(0 0 0.3rem currentColor);
  }
}
.nav-item {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0.75rem 0.25em;
  font-size: 1rem;
  color: var(--text-color-base);
  text-decoration: none;
  background: none;
  border: 0;
  transition-duration: var(--animation-fast);
  transition-property: color, background;

  &.active {
    color: var(--brand-primary);
    background: var(--button-default-hover-background);
  }

  &.active-icon {
    color: var(--brand-primary);
  }

  &:hover {
    background: var(--button-default-hover-background);
  }

  &.disabled {
    color: var(--text-disabled);
  }

  &.danger {
    color: var(--text-danger);
  }

  &.warning {
    color: var(--text-warning);
  }
}

.nav-item-label {
  text-align: center;
  &.attention {
    animation-name: attention;
    animation-duration: 500ms;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    animation-direction: alternate;
  }
}

.nav-item-icon {
  position: relative;
  display: block;
  &.attention {
    animation-name: attention;
    animation-duration: 500ms;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    animation-direction: alternate;
  }
}

.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.icon {
  display: block;
  width: 2rem;
  height: 2rem;
}

.icon-work-action {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0.8rem;
  height: 0.8rem;
  transform: translate(-50%, -40%);
}
</style>
